/**
 * Libraries
 */

import { init, track } from '@plausible-analytics/tracker';

/**
 * Types
 */

import type { LiteralOrString } from '@/shared/types/Helpers';
import type { LocationQuery } from 'vue-router';

const PLAUSIBLE_BLOCKED_HOSTS = ['127.0.0.1'];
let isPlausibleInitialized = false;

const TRACKED_QUERY_PARAMS = ['subTab'] as const;

const UTM_PARAMS = [
  'ref',
  'source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const PAGE_CATEGORIES = [
  'tickets',
  'admin',
  'attendees',
  'advantages',
  'contract',
  'deposit',
] as const;

const PAGE_CATEGORY_PATTERNS: Record<PageCategory, string[]> = {
  tickets: ['/tickets'],
  admin: ['/admin'],
  attendees: ['/attendee', '/attendees'],
  advantages: ['/advantage', '/advantages'],
  contract: ['/contract', '/contracts'],
  deposit: ['/deposit', '/deposits'],
};

const TRACKING_SCHEMA = {
  session_start: {
    screen: 'string' as const,
    screenWidth: 'string' as const,
    screenHeight: 'string' as const,
    screenPixelDepth: 'string' as const,
    screenOrientationType: 'string' as const,
  },
  signing_sheet: {
    method: ['electronic', 'manual'] as const,
  },
  deposit_management: {
    method: ['automatic', 'manual'] as const,
  },
  '404': {
    path: 'string' as const,
  },
} as const;

type TrackingSchema = typeof TRACKING_SCHEMA;
type EventName = keyof TrackingSchema;
type EventProps<E extends EventName> = {
  [K in keyof TrackingSchema[E]]: TrackingSchema[E][K] extends readonly string[]
    ? TrackingSchema[E][K][number]
    : string;
};

type PlausibleHostname = LiteralOrString<typeof PLAUSIBLE_BLOCKED_HOSTS>;
type PageCategory = LiteralOrString<typeof PAGE_CATEGORIES>;

function buildTrackingUrl(path: string, query: LocationQuery): string {
  let url = window.location.origin + path.replace(/\/$/, '');
  for (const param of TRACKED_QUERY_PARAMS) {
    const value = query[param];
    if (typeof value === 'string' && value) {
      url += `/${value}`;
    }
  }

  const searchParams = new URLSearchParams();
  for (const param of UTM_PARAMS) {
    const value = query[param];
    if (typeof value === 'string' && value) {
      searchParams.set(param, value);
    }
  }

  const search = searchParams.toString();
  return search !== '' ? `${url}?${search}` : url;
}

function isExpectedApiHost(apiHost: string, allowedHosts: string): boolean {
  if (!allowedHosts.trim()) return true;

  const hostnames = allowedHosts
    .split(',')
    .map((host) => host.trim())
    .filter(Boolean);

  if (hostnames.length === 0) return true;

  try {
    const parsedHost = new URL(apiHost).hostname;
    return hostnames.includes(parsedHost);
  } catch {
    return false;
  }
}

function setupPlausibleTracking() {
  isPlausibleInitialized = false;
  if (import.meta.env.VITE_PLAUSIBLE_ENABLED !== 'true') return;

  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN?.trim();
  const apiHost = import.meta.env.VITE_PLAUSIBLE_API_HOST?.trim();

  if (!domain || !apiHost) return;

  if (
    !isExpectedApiHost(
      apiHost,
      import.meta.env.VITE_PLAUSIBLE_ALLOWED_API_HOSTS ?? '',
    )
  ) {
    console.warn('[plausible] tracking blocked: unexpected api host');
    return;
  }

  const currentHostname: PlausibleHostname = window.location.hostname;
  const captureOnLocalhost = !PLAUSIBLE_BLOCKED_HOSTS.includes(currentHostname);
  const endpoint = `${apiHost.replace(/\/+$/, '')}/api/event`;

  const config = {
    domain,
    endpoint,
    autoCapturePageviews: false,
    captureOnLocalhost,
  };
  init(config);
  isPlausibleInitialized = true;
  return config;
}

function getPageCategory(path: string): PageCategory {
  const normalizedPath = path.toLowerCase();
  let category: PageCategory;
  let patterns: string[];
  for ([category, patterns] of Object.entries(PAGE_CATEGORY_PATTERNS)) {
    if (patterns.some((pattern) => normalizedPath.includes(pattern))) {
      return category;
    }
  }
  return 'other';
}

function trackEvent<E extends EventName>(event: E, props: EventProps<E>) {
  if (!isPlausibleInitialized) return;
  track(event, { props });
}

function trackPageView(path: string, query: LocationQuery) {
  if (!isPlausibleInitialized) return;
  track('pageview', {
    url: buildTrackingUrl(path, query),
    props: {
      page_category: getPageCategory(path),
    },
  });
}

export { setupPlausibleTracking, trackEvent, trackPageView, getPageCategory };
